import { Query } from "@cubejs-client/core";
import { AppThunk } from "appThunk";
import { Store } from "modules/customer/insights/portfolioNew/store";
import { cubeLoadExtended, ExtendedResultSet } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";
import { PlaceCategory } from "modules/customer/insights/portfolioNew/placeCategory";
import { SizeBucket } from "modules/customer/insights/portfolioNew/sizeBucket";
import { BrandClassification } from "domain/BrandClassification";

export class NearbyStore {
    public readonly clientStoreId: string;
    public readonly id: number;
    public readonly storeName: string;
    public readonly brandName: string | null;
    public readonly placeCategoryId: number;
    public readonly placeSuperCategory: string;
    public readonly sizeBucketId: number;
    public readonly sizeBucketName: string;
    public readonly brandClassification: BrandClassification;

    constructor(
        clientStoreId: string,
        id: number,
        storeName: string,
        brandName: string | null,
        placeCategoryId: number,
        placeSuperCategory: string,
        sizeBucketId: number,
        sizeBucketName: string,
        placeClassification: BrandClassification
    ) {
        this.clientStoreId = clientStoreId;
        this.id = id;
        this.storeName = storeName;
        this.brandName = brandName;
        this.placeCategoryId = placeCategoryId;
        this.placeSuperCategory = placeSuperCategory;
        this.sizeBucketId = sizeBucketId;
        this.sizeBucketName = sizeBucketName;
        this.brandClassification = placeClassification;
    }

    get isIndependent(): boolean {
        return !this.brandName;
    }
}

export const loadNearbyStores = (stores: Store[], placeCategories: PlaceCategory[], sizeBuckets: SizeBucket[]): AppThunk<Promise<NearbyStore[]>> => async (dispatch) => {
    try {
        if (stores.length === 0) {
            return [];
        }
        const placeCategoryMap = new Map<number, PlaceCategory>();
        placeCategories.forEach(pc => placeCategoryMap.set(pc.id, pc));

        const retailCentreIds = stores.map(store => String(store.retailCentreID));

        const query: Query = {
            dimensions: [
                "Mapping_RC_RZ.RetailCentreID",
                "Place_RZ.PlaceID",
                "Place_RZ.PlaceName",
                "Place_RZ.BrandName",
                "Place_RZ.PlaceCategory_ID",
                "Place_RZ.SizeBucketId",
                "Place_RZ.PlaceClassificationName",
            ],
            filters: [{
                member: "Mapping_RC_RZ.RetailCentreID",
                operator: "equals",
                values: retailCentreIds
            }, {
                member: "Place_RZ.PlaceID",
                operator: "set",
            }],
            segments: ["PlaceCategory.IsRetailerCategory"],
            limit: 50000
        };

        // Temporarily using cubeLoadExtended until we introduce a filter on isDestination and remove the mapping from RC to RZ
        // After the above two tasks are completed, the number of records returned should be low enough to use cubeLoad
        const resultSet = await dispatch(cubeLoadExtended(query)) as unknown as ExtendedResultSet<any>;
        const rawData = resultSet.loadResponses[0].data;
        const nearbyStores: NearbyStore[] = [];
        stores.forEach(store => {
            const relevantRows = rawData.filter(row => Number(row["Mapping_RC_RZ.RetailCentreID"]) === store.retailCentreID);
            const relevantNearbyStores = relevantRows.map(row => {
                const placeCategoryId = Number(row["Place_RZ.PlaceCategory_ID"]);
                const superCategoryName = placeCategoryMap.get(placeCategoryId)?.superCategoryName ?? "Unknown";
                const sizeBucketLabel = sizeBuckets.find(bucket => bucket.id === Number(row["Place_RZ.SizeBucketId"]))?.label ?? "Unknown";
                return new NearbyStore(
                    String(store.id),
                    Number(row["Place_RZ.PlaceID"]),
                    row["Place_RZ.PlaceName"],
                    row["Place_RZ.BrandName"],
                    Number(placeCategoryId),
                    superCategoryName,
                    Number(row["Place_RZ.SizeBucketId"]),
                    sizeBucketLabel,
                    BrandClassification.fromString(row["Place_RZ.PlaceClassificationName"])
                );
            });
            nearbyStores.push(...relevantNearbyStores);
        });
        return nearbyStores;
    } catch (error) {
        dispatch(logError("Error loading NearbyStores.", error));
        throw error;
    }
};
